import React from "react";
import {
  MapPin,
  Mountain,
  Bike,
  Dumbbell,
  GraduationCap,
  Sparkles,
  Calendar,
} from "lucide-react";

const TIMELINE = [
  {
    year: "2014",
    title: "Start in der Fitnessindustrie",
    text: "Mein Weg beginnt – als Trainer mit dem Anspruch, Menschen wirklich weiterzubringen statt nur Trainingspläne durchzuziehen.",
  },
  {
    year: "2016",
    title: "Entdeckung des funktionellen Trainings",
    text: "Eine neue Facette, die mein Denken verändert: Qualität vor Quantität. Ich verschlinge Bücher, besuche Fortbildungen, baue mein Verständnis grundauf neu.",
  },
  {
    year: "2018",
    title: "Duales Studium Sportökonomie abgeschlossen",
    text: "Bachelor of Arts Sportökonomie. Parallel: Fitness-Trainer B-Lizenz und später die Athletiktrainer A-Lizenz – fachliche Tiefe, auf die ich heute aufbaue.",
  },
  {
    year: "2025",
    title: "Zertifizierter Mikronährstoffcoach",
    text: "Nach Jahren intensiver Auseinandersetzung mit Mikronährstoffen – und dem Ausgleich meiner eigenen Mängel – schließe ich den Mikronährstoffcoach ab. Seitdem biete ich testbasierte Beratungen an.",
  },
];

const HOBBIES = [
  { icon: Mountain, label: "Klettern" },
  { icon: Bike, label: "Radfahren" },
  { icon: Dumbbell, label: "Funktionelles Training" },
];

const QUALIS = [
  "Bachelor of Arts – Sportökonomie",
  "Fitness-Trainer B-Lizenz",
  "Athletiktrainer A-Lizenz",
  "Zertifizierter Mikronährstoffcoach",
];

export default function AboutMeSection() {
  return (
    <section
      id="about-me-section"
      data-testid="about-me-section"
      className="relative bg-[#F7F9FB] text-[#1F2937] py-24 lg:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Intro */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start mb-16 lg:mb-24">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-2 rounded-[2rem] bg-[#6FB04F]/15 -z-10 rotate-[-2deg]" />
              <img
                src="https://static.wixstatic.com/media/ec825b_9ed2e87041c048e3b1234af0a5ce6981~mv2.jpg/v1/fill/w_941,h_1105,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Alex%20Buchner-0025-%C2%A9Fotograf%20Hans%20Breiten.jpg"
                alt="Alex Buchner – Personal Trainer & Mikronährstoffcoach"
                className="rounded-[2rem] w-full h-[460px] lg:h-[600px] object-cover"
                loading="lazy"
              />
              <div className="absolute -bottom-4 -right-4 lg:-bottom-6 lg:-right-6 bg-[#1F2937] text-[#F7F9FB] rounded-2xl px-5 py-4 shadow-xl max-w-[80%]">
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#6FB04F] font-semibold mb-1">
                  Seit 2014
                </p>
                <p className="font-display text-sm font-bold leading-snug">
                  In der Fitnessbranche zuhause – mit Tiefe statt Trend.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-[#2E89C4] mb-5">
              Über mich
            </p>
            <h2
              data-testid="about-title"
              className="font-display text-3xl sm:text-4xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]"
            >
              Hi, ich bin <span className="text-[#2E89C4]">Alex.</span>
            </h2>
            <p className="mt-6 text-lg lg:text-xl text-[#1F2937]/75 leading-relaxed">
              Schon seit jeher liebe ich es, mit Menschen zusammenzuarbeiten und
              ihnen zu helfen, ihre Ziele zu erreichen – egal ob
              Gewichtsreduktion, Muskelaufbau, schmerzfrei werden oder einfach
              wieder die heimischen Berge erklimmen.
            </p>
            <p className="mt-5 text-base lg:text-lg text-[#1F2937]/70 leading-relaxed">
              Seit über zehn Jahren begleite ich Menschen im Training. 2016 hat
              das funktionelle Training mein Denken verändert –{" "}
              <strong>Qualität vor Quantität</strong>. Und seit ich vor ein paar
              Jahren angefangen habe, mich tief mit Mikronährstoffen zu
              beschäftigen, weiß ich: Training allein ist nur die halbe
              Geschichte.
            </p>

            <p
              className="mt-7 text-base lg:text-lg font-display italic text-[#1F2937]/80 border-l-2 border-[#2E89C4] pl-5"
              data-testid="about-quote"
            >
              „Ich freue mich darauf, dir ein Stück Lebensfreude zurückzugeben
              und dir zu mehr Gesundheit zu verhelfen."
            </p>

            <div className="mt-9 flex items-center gap-3 text-sm text-[#1F2937]/65">
              <MapPin size={16} className="text-[#6FB04F]" />
              Tätig in Traunstein, rund um den Chiemsee und im südöstlichen
              Chiemgau – vor Ort und online.
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <p className="text-xs sm:text-sm font-semibold tracking-[0.18em] uppercase text-[#6FB04F] mb-4">
              Mein Weg
            </p>
            <h3 className="font-display text-2xl lg:text-3xl font-bold leading-tight">
              Über zehn Jahre Erfahrung – fachlich tief, ehrlich gelernt.
            </h3>
            <p className="mt-5 text-sm text-[#1F2937]/65 leading-relaxed">
              Kein Quereinstieg, kein Online-Kurs-Trainer. Jede Lizenz, jede
              Erkenntnis ist über Jahre gewachsen.
            </p>
          </div>

          <div className="lg:col-span-8">
            <ol className="relative space-y-7">
              <span
                aria-hidden="true"
                className="absolute left-[10px] top-2 bottom-2 w-px bg-[#1F2937]/15"
              />
              {TIMELINE.map((t, i) => (
                <li
                  key={i}
                  data-testid={`timeline-item-${i}`}
                  className="relative pl-10"
                >
                  <span className="absolute left-0 top-1.5 h-5 w-5 rounded-full bg-[#F7F9FB] border-2 border-[#2E89C4] flex items-center justify-center">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#2E89C4]" />
                  </span>
                  <div className="flex items-center gap-3 mb-1.5">
                    <Calendar size={12} className="text-[#6FB04F]" />
                    <span className="text-xs font-bold tracking-[0.15em] uppercase text-[#6FB04F]">
                      {t.year}
                    </span>
                  </div>
                  <h4 className="font-display text-lg lg:text-xl font-bold">
                    {t.title}
                  </h4>
                  <p className="mt-2 text-[15px] text-[#1F2937]/75 leading-relaxed">
                    {t.text}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Qualifications + Hobbies */}
        <div className="mt-20 lg:mt-24 grid md:grid-cols-2 gap-6 lg:gap-8">
          <div
            data-testid="qualifications-card"
            className="rounded-2xl border border-[#1F2937]/10 bg-white/70 backdrop-blur-sm p-7 lg:p-8"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-lg bg-[#2E89C4]/10 flex items-center justify-center">
                <GraduationCap
                  size={18}
                  className="text-[#2E89C4]"
                  strokeWidth={1.8}
                />
              </div>
              <h3 className="font-display text-xl font-bold">
                Qualifikationen
              </h3>
            </div>
            <ul className="space-y-2.5">
              {QUALIS.map((q, i) => (
                <li
                  key={i}
                  data-testid={`quali-${i}`}
                  className="flex items-start gap-3 text-[15px] text-[#1F2937]/80"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#2E89C4] shrink-0" />
                  {q}
                </li>
              ))}
            </ul>
          </div>

          <div
            data-testid="hobbies-card"
            className="rounded-2xl border border-[#1F2937]/10 bg-[#1F2937] text-[#F7F9FB] p-7 lg:p-8"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-lg bg-[#6FB04F]/15 flex items-center justify-center">
                <Sparkles size={18} className="text-[#6FB04F]" strokeWidth={1.8} />
              </div>
              <h3 className="font-display text-xl font-bold">
                Privat – wo du mich findest
              </h3>
            </div>
            <p className="text-[15px] text-[#F7F9FB]/70 leading-relaxed mb-5">
              In meiner Freizeit bin ich meist draußen – am Berg, auf dem Rad
              oder beim funktionellen Training. Die Berge sind nicht nur
              Hintergrund meiner Arbeit, sie sind mein Spielplatz.
            </p>
            <div className="flex flex-wrap gap-2">
              {HOBBIES.map((h, i) => {
                const Icon = h.icon;
                return (
                  <span
                    key={i}
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#F7F9FB]/[0.07] border border-[#F7F9FB]/15 text-sm"
                  >
                    <Icon size={14} className="text-[#6FB04F]" />
                    {h.label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
